import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useStore } from '../lib/store.js';
import { bytes, bps, pct, uptime } from '../lib/format.js';
import ServerCard from '../components/ServerCard.js';
import { IconActivity, IconCpu, IconPlus, IconServer, IconUsers } from '../components/Icons.js';

interface FleetSample { ts: number; players: number; cpu: number }
const FLEET_BUF_LEN = 60; // ~30 min at 30s sampling

export default function Dashboard() {
  const servers = useStore((s) => s.servers);
  const live = useStore((s) => s.liveStats);
  const machines = useStore((s) => s.machines);
  const nodeStatus = useStore((s) => s.nodeStatus);

  const machineNames = Array.from(new Set([
    ...nodeStatus.keys(),
    ...Array.from(servers.values()).map((s) => s.Server_Machine),
  ])).sort();

  const allServers = Array.from(servers.values()).sort((a, b) => {
    if (a.Server_Machine !== b.Server_Machine) return a.Server_Machine.localeCompare(b.Server_Machine);
    return a.Server_Name.localeCompare(b.Server_Name);
  });

  const onlineCount = Array.from(live.values()).filter((s) => s.status === 'online').length;
  const totalPlayers = Array.from(live.values()).reduce((acc, s) => acc + (s.rcon?.Players ?? 0), 0);
  const totalMaxPlayers = Array.from(live.values()).reduce((acc, s) => acc + (s.rcon?.MaxPlayers ?? 0), 0);
  const machinesOnline = machineNames.filter((n) => nodeStatus.get(n)?.online).length;
  const cpuAvg = avgCpu(machines);

  // Live fleet rolling buffer — samples the aggregates every 30s for a 30-min
  // strip below the headline tiles. Keeps backend cost zero; persistent
  // history lives on the per-machine and per-server pages.
  const [fleet, setFleet] = useState<FleetSample[]>([]);
  const lastSnap = useRef({ players: 0, cpu: 0 });
  lastSnap.current = { players: totalPlayers, cpu: cpuAvg };
  useEffect(() => {
    const tick = () => setFleet((cur) => {
      const next: FleetSample = { ts: Date.now(), ...lastSnap.current };
      const out = [...cur, next];
      while (out.length > FLEET_BUF_LEN) out.shift();
      return out;
    });
    tick();
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-fg-muted mt-1">Real-time fleet overview.</p>
      </header>

      {/* Aggregate stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Tile
          icon={<IconServer />}
          label="Servers Online"
          value={`${onlineCount}`}
          sub={`of ${allServers.length} configured`}
          tone={onlineCount === allServers.length && allServers.length > 0 ? 'good' : 'neutral'}
        />
        <Tile
          icon={<IconUsers />}
          label="Players"
          value={`${totalPlayers}`}
          sub={totalMaxPlayers > 0 ? `${totalMaxPlayers} slots` : 'across all servers'}
        />
        <Tile
          icon={<IconActivity />}
          label="Machines"
          value={`${machinesOnline}/${machineNames.length}`}
          sub="agents online"
          tone={machinesOnline < machineNames.length ? 'warn' : 'neutral'}
        />
        <Tile
          icon={<IconCpu />}
          label="Aggregate CPU"
          value={pct(cpuAvg)}
          sub={`${machines.size} reporting`}
        />
      </section>

      {/* Fleet activity */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <FleetChart label="Players · last 30 min" dataKey="players" data={fleet} stroke="#34d399" suffix="" />
        <FleetChart label="Aggregate CPU · last 30 min" dataKey="cpu" data={fleet} stroke="#5fa8ff" max={100} suffix="%" />
      </section>

      {/* Machines */}
      <section>
        <header className="flex items-baseline justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-fg">Machines</h2>
            <p className="text-xs text-fg-dim mt-0.5">Real-time telemetry</p>
          </div>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {machineNames.map((name) => {
            const tel = machines.get(name);
            const online = !!nodeStatus.get(name)?.online;
            const machineServers = allServers.filter((s) => s.Server_Machine === name);
            const memPct = tel ? (tel.memory.used_bytes / tel.memory.total_bytes) * 100 : 0;
            const diskMaxPct = tel ? Math.max(0, ...tel.disks.map((d) => (d.used_bytes / d.total_bytes) * 100)) : 0;
            const tone = !online ? '' : worstTone(tel?.cpu.load_pct ?? 0, memPct, diskMaxPct);
            return (
              <Link
                key={name}
                to={`/machines/${encodeURIComponent(name)}`}
                className={`card card-hover p-4 group cursor-pointer ${tone}`}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-fg group-hover:text-accent transition-colors flex items-center gap-2 truncate">
                      <span className={`dot shrink-0 ${online ? 'bg-status-online' : 'bg-status-error'}`} />
                      <span className="truncate">{name}</span>
                    </h3>
                    <p className={`text-xs mt-1 ${online ? 'text-fg-muted' : 'text-status-error'}`}>
                      {machineServers.length} server{machineServers.length === 1 ? '' : 's'} · {online ? 'online' : 'unreachable'}
                    </p>
                  </div>
                  {online && tel && (
                    <span className="text-xs text-fg-dim tabular-nums shrink-0">
                      up {uptime(tel.uptime_sec)}
                    </span>
                  )}
                </div>

                {online && tel ? (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
                    <Stat label="CPU" value={pct(tel.cpu.load_pct)} bar={tel.cpu.load_pct} />
                    <Stat label="RAM" value={pct((tel.memory.used_bytes / tel.memory.total_bytes) * 100)} bar={(tel.memory.used_bytes / tel.memory.total_bytes) * 100} />
                    <Stat label="Net In" value={bps(tel.network.rx_bytes_per_sec)} />
                    <Stat label="Net Out" value={bps(tel.network.tx_bytes_per_sec)} />
                  </div>
                ) : (
                  <div className="text-xs text-fg-dim italic py-2">
                    {online ? 'No telemetry yet…' : 'Agent disconnected — telemetry unavailable'}
                  </div>
                )}

                {online && tel && tel.disks.length > 0 && (
                  <div className="mt-3.5 pt-3 border-t border-bg-border space-y-1.5">
                    {tel.disks.slice(0, 2).map((d) => (
                      <div key={d.mount} className="flex items-center gap-2 text-xs">
                        <span className="text-fg-dim w-12 font-mono">{d.mount}</span>
                        <DiskBar pct={(d.used_bytes / d.total_bytes) * 100} />
                        <span className="text-fg-muted tabular-nums text-[11px]">
                          {bytes(d.used_bytes)} <span className="text-fg-dim">/ {bytes(d.total_bytes)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
          {machineNames.length === 0 && (
            <EmptyState
              icon={<IconActivity className="w-6 h-6" />}
              title="No machines registered yet"
              hint={<>Register one in <Link to="/settings" className="text-accent hover:underline">Settings → Machines</Link>.</>}
            />
          )}
        </div>
      </section>

      {/* All servers */}
      <section>
        <header className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-fg">Servers</h2>
            <p className="text-xs text-fg-dim mt-0.5">{allServers.length} configured</p>
          </div>
          <Link to="/servers/new" className="btn-ghost btn-sm">
            <IconPlus className="w-3.5 h-3.5" /> New Server
          </Link>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {allServers.map((s) => (
            <ServerCard
              key={s.Server_ID}
              server={s}
              live={live.get(s.Server_ID)}
              nodeOnline={!!nodeStatus.get(s.Server_Machine)?.online}
            />
          ))}
          {allServers.length === 0 && (
            <EmptyState
              icon={<IconServer className="w-6 h-6" />}
              title="No servers configured yet"
              hint={<Link to="/servers/new" className="text-accent hover:underline">Create one →</Link>}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function Tile({
  icon, label, value, sub, tone = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  tone?: 'neutral' | 'good' | 'warn';
}) {
  // 'good' uses the brand accent so the celebration tint follows the user's
  // configured theme. 'warn' stays on status-warn (a fixed semantic color).
  const ringTone =
    tone === 'good' ? 'ring-accent/30 bg-accent/5'
      : tone === 'warn' ? 'ring-status-warn/30 bg-status-warn/5'
      : 'bg-bg-card';
  const iconTone =
    tone === 'good' ? 'text-accent'
      : tone === 'warn' ? 'text-status-warn'
      : 'text-fg-dim';

  return (
    <div className={`card px-4 py-4 flex flex-col gap-2 ${tone !== 'neutral' ? `ring-1 ${ringTone}` : ''}`}>
      <div className="flex items-center justify-between">
        <span className="stat-label">{label}</span>
        <span className={iconTone}>{icon}</span>
      </div>
      <div className="stat-value">{value}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

function Stat({ label, value, bar }: { label: string; value: string; bar?: number }) {
  const valueTone =
    bar != null && bar > 90 ? 'text-status-error'
      : bar != null && bar > 75 ? 'text-status-warn'
      : 'text-fg';
  return (
    <div>
      <div className="flex items-center justify-between text-fg-dim">
        <span>{label}</span>
        <span className={`tabular-nums font-medium ${valueTone}`}>{value}</span>
      </div>
      {bar !== undefined && (
        <div className="h-1 mt-1 bg-bg-border rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${barColor(bar)}`}
            style={{ width: `${Math.max(0, Math.min(100, bar))}%` }}
          />
        </div>
      )}
    </div>
  );
}

function barColor(pct: number) {
  if (pct > 90) return 'bg-status-error';
  if (pct > 75) return 'bg-status-warn';
  return 'bg-accent';
}

/** Subtle ring tint when a machine has anything pegged. Disk uses a higher
 *  bar (>90 critical, >80 warn) since it's a slower-moving metric. */
function worstTone(cpu: number, mem: number, disk: number): string {
  const critical = cpu > 90 || mem > 90 || disk > 90;
  const warn = cpu > 75 || mem > 80 || disk > 80;
  if (critical) return 'ring-1 ring-status-error/40 bg-status-error/5';
  if (warn)     return 'ring-1 ring-status-warn/30 bg-status-warn/5';
  return '';
}

function DiskBar({ pct }: { pct: number }) {
  const safe = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex-1 h-1 bg-bg-border rounded-full overflow-hidden">
      <div className={`h-full ${barColor(safe)} transition-all duration-500`} style={{ width: `${safe}%` }} />
    </div>
  );
}

function EmptyState({ icon, title, hint }: { icon: React.ReactNode; title: string; hint: React.ReactNode }) {
  return (
    <div className="card p-10 col-span-full flex flex-col items-center text-center gap-3">
      <div className="w-12 h-12 rounded-full bg-bg-raised border border-bg-border flex items-center justify-center text-fg-dim">
        {icon}
      </div>
      <div>
        <div className="text-sm font-medium text-fg">{title}</div>
        <div className="text-xs text-fg-muted mt-1">{hint}</div>
      </div>
    </div>
  );
}

function FleetChart({
  label, dataKey, data, stroke, max, suffix,
}: {
  label: string; dataKey: 'players' | 'cpu'; data: FleetSample[]; stroke: string; max?: number; suffix: string;
}) {
  const last = data.length > 0 ? data[data.length - 1][dataKey] : 0;
  const lastDisplay = dataKey === 'cpu' ? last.toFixed(0) : Math.round(last).toString();
  const id = `dashfleet-${dataKey}`;
  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-fg-dim font-semibold">{label}</span>
        <span className="text-lg font-semibold tabular-nums text-fg tracking-tight">
          {lastDisplay}{suffix}
        </span>
      </div>
      <div className="h-24">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-fg-dim italic">Collecting samples…</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="ts" tick={{ fontSize: 10, fill: '#646b7a' }} tickLine={false} axisLine={{ stroke: '#262b3a' }}
                tickFormatter={(v) => new Date(v as number).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                minTickGap={32} />
              <YAxis tick={{ fontSize: 10, fill: '#646b7a' }} tickLine={false} axisLine={false}
                domain={max != null ? [0, max] : ['auto', 'auto']}
                width={32}
                tickFormatter={(v) => (typeof v === 'number' ? Math.round(v).toString() : v)} />
              <Tooltip
                contentStyle={{ background: '#161a24', border: '1px solid #262b3a', borderRadius: 8, fontSize: 11, padding: '6px 10px' }}
                labelFormatter={(v) => new Date(v as number).toLocaleTimeString()}
                formatter={(v: number) => [`${dataKey === 'cpu' ? v.toFixed(0) : Math.round(v)}${suffix}`, label]}
              />
              <Area type="monotone" dataKey={dataKey} stroke={stroke} strokeWidth={1.75} fill={`url(#${id})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function avgCpu(machines: Map<string, { cpu: { load_pct: number } }>): number {
  if (machines.size === 0) return 0;
  let total = 0;
  for (const m of machines.values()) total += m.cpu.load_pct;
  return total / machines.size;
}
